import React, { useState, useEffect } from "react";
import {
  ArrowLeft, MessageSquare, Heart, Share2, Flag, MapPin, Clock,
  CheckCircle2, ShieldCheck, Loader2, ChevronRight,
} from "lucide-react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import PageSeo from "../SEO/PageSeo";
import {
  API_URL, buildTheme, formatName, categoryLabel, isVerifiedListing,
  getInitials, timeAgo, ListingAttributes, ProductGallery,
  InlineSidebarChat, PremiumMessagingCenter,
} from "./marketplaceShared";

export default function ProductDetailPage({ isDarkMode, toggleTheme }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [item, setItem] = useState(location.state?.listing || null);
  const [isLoading, setIsLoading] = useState(!location.state?.listing);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [showDesktopChat, setShowDesktopChat] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, title: "", message: "" });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Always fetch the canonical listing (covers direct links / refreshes / stale state)
  useEffect(() => {
    let cancelled = false;
    const fetchListing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tradelink/listings/${id}/`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) { setItem(data); setNotFound(false); }
        } else if (res.status === 404 && !cancelled) {
          if (!location.state?.listing) setNotFound(true);
        }
      } catch (err) {
        console.error("Failed to load listing:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchListing();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Related listings from the same category
  useEffect(() => {
    if (!item?.category) return;
    let cancelled = false;
    const fetchRelated = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tradelink/listings/`);
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
          const filtered = list.filter((l) => l.category === item.category && String(l.id) !== String(item.id)).slice(0, 4);
          if (!cancelled) setRelated(filtered);
        }
      } catch (err) {}
    };
    fetchRelated();
    return () => { cancelled = true; };
  }, [item?.category, item?.id]);

  const triggerAuthRequired = () => navigate("/login", { state: { redirectTo: `/marketplace/listing/${id}` } });

  const triggerSubscriptionModal = () => {
    setShowDesktopChat(false);
    setShowMobileChat(false);
    setModalConfig({
      isOpen: true,
      title: "Action Required",
      message: "Your free trial has expired or your membership is inactive. Please set up your automated monthly billing to continue using the messaging features.",
    });
  };

  const handleChatClick = () => {
    const token = localStorage.getItem("token");
    if (!token) { triggerAuthRequired(); return; }
    if (isMobile) setShowMobileChat(true);
    else setShowDesktopChat(true);
  };

  const theme = buildTheme(isDarkMode);
  const { D, bg, panelBg, midBg, border, text, textMid, textSoft, amber, amberL } = theme;

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", background: bg }}>
        <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "120px 24px" }}>
          <Loader2 size={36} style={{ color: amber, animation: "spin 1s linear infinite", marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: textMid, fontWeight: 600 }}>Loading listing…</div>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
      </div>
    );
  }

  if (notFound || !item) {
    return (
      <div style={{ minHeight: "100vh", background: bg }}>
        <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "120px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: text, marginBottom: 8 }}>Listing not found</div>
          <div style={{ fontSize: 14, color: textMid, marginBottom: 24 }}>This item may have been sold or removed.</div>
          <Link to="/marketplace" style={{ padding: "12px 24px", borderRadius: 10, background: `linear-gradient(135deg,${amber},${amberL})`, color: "#0d1f3c", fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
            Back to Marketplace
          </Link>
        </div>
        <Footer isDarkMode={isDarkMode} />
      </div>
    );
  }

  const sellerName = formatName(item.seller);
  const pageTitle = `${item.title} | Pilot Car Equipment | PilotLink USA`;
  const pageDescription = item.description ? `${item.description.substring(0, 150)}...` : `Buy ${item.title} from verified pilot car equipment sellers on PilotLink USA.`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: item.title,
    description: item.description || item.title,
    image: item.images?.[0]?.image || "https://pilotlinkusa.org/logo192.png",
    offers: {
      "@type": "Offer",
      price: item.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      seller: { "@type": "Person", name: sellerName },
    },
  };

  return (
    <div style={{ "--mkt-midbg": midBg, minHeight: "100vh", background: bg, fontFamily: "'Inter',sans-serif" }}>
      <PageSeo title={pageTitle} description={pageDescription} canonicalUrl={`/marketplace/listing/${item.id}`} schema={productSchema} />
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "0 0 32px" : "20px clamp(12px,3vw,24px) 48px" }}>

        {/* BREADCRUMB / BACK */}
        {!isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: textSoft, marginBottom: 16, flexWrap: "wrap" }}>
            <Link to="/marketplace" style={{ color: textMid, textDecoration: "none", fontWeight: 600 }}>Marketplace</Link>
            <ChevronRight size={13} />
            <Link to={`/marketplace?category=${item.category}`} style={{ color: textMid, textDecoration: "none", fontWeight: 600 }}>{categoryLabel(item.category)}</Link>
            <ChevronRight size={13} />
            <span style={{ color: textSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>{item.title}</span>
          </div>
        )}
        {isMobile && (
          <div style={{ position: "sticky", top: 0, zIndex: 20, background: panelBg, borderBottom: `1px solid ${border}`, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", border: "none", background: midBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: text }}>
              <ArrowLeft size={17} />
            </button>
            <div style={{ fontSize: 14, fontWeight: 800, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
          </div>
        )}

        <div style={{ display: "flex", gap: 32, alignItems: "flex-start", padding: isMobile ? 0 : undefined, flexWrap: isMobile ? "wrap" : "nowrap" }}>

          {/* LEFT: GALLERY + DESCRIPTION */}
          <div style={{ flex: isMobile ? "1 1 100%" : "1 1 62%", minWidth: 0 }}>
            <div style={{ position: "relative" }}>
              <ProductGallery images={item.images} alt={item.title} isMobile={isMobile} />
              {item.condition && (
                <div style={{ position: "absolute", top: 14, left: isMobile ? 14 : 14, padding: "5px 11px", borderRadius: 7, background: "rgba(15,23,42,0.70)", color: "#fff", fontSize: 11, fontWeight: 800, backdropFilter: "blur(4px)", textTransform: "uppercase", letterSpacing: ".06em" }}>
                  {item.condition}
                </div>
              )}
              {!isMobile && (
                <div style={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 8 }}>
                  <button onClick={() => setIsSaved((s) => !s)} title="Save" style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: "rgba(15,23,42,0.65)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}>
                    <Heart size={16} style={{ color: isSaved ? "#ef4444" : "#fff", fill: isSaved ? "#ef4444" : "none" }} />
                  </button>
                  <button onClick={() => navigator.share ? navigator.share({ title: item.title, url: window.location.href }) : navigator.clipboard.writeText(window.location.href)} title="Share" style={{ width: 38, height: 38, borderRadius: "50%", border: "none", background: "rgba(15,23,42,0.65)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(4px)" }}>
                    <Share2 size={16} style={{ color: "#fff" }} />
                  </button>
                </div>
              )}
            </div>

            <div style={{ padding: isMobile ? "18px 16px 0" : "24px 0 0" }}>
              {/* MOBILE: title/price block sits under gallery */}
              {isMobile && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 800, color: amber, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 6 }}>{categoryLabel(item.category)}</div>
                  <h1 style={{ fontSize: 21, fontWeight: 900, color: text, margin: "0 0 6px", lineHeight: 1.25 }}>{item.title}</h1>
                  <div style={{ fontSize: 27, fontWeight: 900, color: amber, fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 10 }}>${Number(item.price).toLocaleString()}</div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                    {item.location && <div style={{ fontSize: 13, color: textMid, display: "flex", alignItems: "center", gap: 5 }}><MapPin size={13} />{item.location}{item.zipcode ? `, ${item.zipcode}` : ""}</div>}
                    {item.created_at && <div style={{ fontSize: 12, color: textSoft, display: "flex", alignItems: "center", gap: 4 }}><Clock size={12} />{timeAgo(item.created_at)}</div>}
                  </div>
                </>
              )}

              <div style={{ fontSize: 13, fontWeight: 800, color: text, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${border}` }}>Description</div>
              {item.description ? (
                <p style={{ fontSize: 14.5, color: textMid, lineHeight: 1.8, marginBottom: 8, whiteSpace: "pre-wrap" }}>{item.description}</p>
              ) : (
                <p style={{ fontSize: 14, color: textSoft, fontStyle: "italic" }}>No description provided.</p>
              )}

              <ListingAttributes item={item} />

              {/* SAFETY NOTE */}
              <div style={{ marginTop: 20, padding: "12px 14px", borderRadius: 12, background: midBg, border: `1px solid ${border}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
                <ShieldCheck size={16} style={{ color: amber, flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12.5, color: textSoft, lineHeight: 1.6 }}>
                  Meet in a public place, inspect equipment before paying, and never wire money to a stranger. Report suspicious listings to PilotLink support.
                </div>
              </div>
            </div>

            {/* RELATED LISTINGS */}
            {related.length > 0 && (
              <div style={{ padding: isMobile ? "24px 16px 0" : "36px 0 0" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: text, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 14 }}>More in {categoryLabel(item.category)}</div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 12 }}>
                  {related.map((r) => (
                    <div key={r.id} onClick={() => navigate(`/marketplace/listing/${r.id}`, { state: { listing: r } })}
                      style={{ background: panelBg, borderRadius: 12, border: `1px solid ${border}`, overflow: "hidden", cursor: "pointer", transition: "transform .15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
                    >
                      <div style={{ height: 100, background: midBg }}>
                        {r.images?.length > 0
                          ? <img src={r.images[0].image} alt={r.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📦</div>}
                      </div>
                      <div style={{ padding: "8px 10px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 3 }}>{r.title}</div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: amber }}>${Number(r.price).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: STICKY PRICE / SELLER / ACTIONS (desktop only — mobile uses bottom bar) */}
          {!isMobile && (
            <div style={{ flex: "0 0 320px", position: "sticky", top: 84 }}>
              <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 16, padding: 20, boxShadow: D ? "0 4px 20px rgba(0,0,0,0.25)" : "0 4px 20px rgba(13,31,60,0.08)" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: amber, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>{categoryLabel(item.category)}</div>
                <h1 style={{ fontSize: 20, fontWeight: 900, color: text, margin: "0 0 8px", lineHeight: 1.3 }}>{item.title}</h1>
                <div style={{ fontSize: 30, fontWeight: 900, color: amber, fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 12 }}>${Number(item.price).toLocaleString()}</div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                  {item.location && <div style={{ fontSize: 13, color: textMid, display: "flex", alignItems: "center", gap: 6 }}><MapPin size={14} />{item.location}{item.zipcode ? `, ${item.zipcode}` : ""}</div>}
                  {item.created_at && <div style={{ fontSize: 12, color: textSoft, display: "flex", alignItems: "center", gap: 6 }}><Clock size={13} />Listed {timeAgo(item.created_at)}</div>}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, background: midBg, border: `1px solid ${border}`, marginBottom: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", background: `${amber}20`, border: `2px solid ${amber}30`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, color: amber, flexShrink: 0 }}>{getInitials(sellerName)}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sellerName}</div>
                    {isVerifiedListing(item) ? (
                      <div style={{ fontSize: 12, color: "#22c55e", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}><CheckCircle2 size={13} /> Verified Seller</div>
                    ) : (
                      <div style={{ fontSize: 12, color: textSoft, fontWeight: 600 }}>Unverified Seller</div>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 9, marginBottom: 10 }}>
                  <button onClick={handleChatClick}
                    style={{ flex: 1, padding: "12px", borderRadius: 11, border: `2px solid ${amber}`, background: "transparent", color: amber, fontWeight: 800, fontSize: 13.5, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
                    <MessageSquare size={15} /> Ask
                  </button>
                  <button onClick={handleChatClick}
                    style={{ flex: 1.4, padding: "12px", borderRadius: 11, border: "none", cursor: "pointer", background: `linear-gradient(135deg,${amber},${amberL})`, color: "#0d1f3c", fontWeight: 800, fontSize: 13.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit", boxShadow: `0 4px 14px ${amber}40` }}>
                    Make Offer
                  </button>
                </div>
                <button style={{ width: "100%", padding: "9px", borderRadius: 10, border: "none", background: "transparent", color: textSoft, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, fontFamily: "inherit" }}>
                  <Flag size={12} /> Report this listing
                </button>
              </div>

              {/* DESKTOP FLOATING CHAT PANEL */}
              {showDesktopChat && (
                <div style={{ marginTop: 16 }}>
                  <InlineSidebarChat currentItem={item} onClose={() => setShowDesktopChat(false)} onSubscriptionError={triggerSubscriptionModal} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MOBILE STICKY ACTION BAR */}
      {isMobile && (
        <div style={{ position: "sticky", bottom: 0, zIndex: 20, background: panelBg, borderTop: `1px solid ${border}`, padding: "12px 16px", display: "flex", gap: 10 }}>
          <button onClick={() => setIsSaved((s) => !s)} style={{ width: 48, height: 48, borderRadius: 12, border: `1.5px solid ${border}`, background: midBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <Heart size={18} style={{ color: isSaved ? "#ef4444" : text, fill: isSaved ? "#ef4444" : "none" }} />
          </button>
          <button onClick={handleChatClick}
            style={{ flex: 1, padding: "12px", borderRadius: 12, border: `2px solid ${amber}`, background: "transparent", color: amber, fontWeight: 800, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
            <MessageSquare size={16} /> Ask
          </button>
          <button onClick={handleChatClick}
            style={{ flex: 1.4, padding: "12px", borderRadius: 12, border: "none", cursor: "pointer", background: `linear-gradient(135deg,${amber},${amberL})`, color: "#0d1f3c", fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit", boxShadow: `0 4px 14px ${amber}40` }}>
            Make Offer
          </button>
        </div>
      )}

      {showMobileChat && <PremiumMessagingCenter currentItem={item} onClose={() => setShowMobileChat(false)} onSubscriptionError={triggerSubscriptionModal} />}

      {/* SUBSCRIPTION / ERROR MODAL */}
      {modalConfig.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-500">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{modalConfig.title}</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-6">{modalConfig.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setModalConfig({ ...modalConfig, isOpen: false })} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Cancel</button>
              <button onClick={() => { setModalConfig({ ...modalConfig, isOpen: false }); navigate("/setup-billing"); }} className="flex-1 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-colors shadow-sm">Setup Billing</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg);}}
        .hide-scrollbar::-webkit-scrollbar{display:none;}
        .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none;}
      `}</style>

      {!isMobile && <Footer isDarkMode={isDarkMode} />}
    </div>
  );
}
